/*
 * FractalAI bilingual rollout header.
 *
 * 文件定位 / File Purpose
 * - 中文：定义全局研究系统样式，包括研究壳布局、右侧上下文栏、页面宽度、响应式隐藏规则和基础视觉变量。
 * - English: Defines global research-system styling, including the research shell, context rail, page width, responsive hiding rules, and base visual tokens.
 *
 * 调用关系 / Call Flow
 * - 中文：由 Umi 运行时 app.ts/app.jsx 在根容器渲染时挂载，影响正式研究系统的大部分页面。
 * - English: Mounted by the Umi runtime app.ts/app.jsx at the root container and affects most formal research-system pages.
 *
 * 数据与业务边界 / Data and Business Boundaries
 * - 中文：仅运行在浏览器侧或 Umi 构建侧，不直接访问数据库、不写 SQL、不复制服务端分型核心算法。
 * - English: Runs only in the browser or Umi build layer; it does not access databases, write SQL, or copy server-side fractal-core algorithms.
 *
 * 上线注意 / Release Notes
 * - 中文：作为智能研究工作台前端上线清单的一部分，修改时必须保持路由、白名单登录、报告/策略入口和生产构建校验一致。
 * - English: Part of the intelligence-workbench frontend rollout scope; keep routes, whitelist login, report/strategy entry points, and production-build checks aligned.
 */

import { createGlobalStyle } from 'antd-style';

const GlobalStyle = createGlobalStyle`
  html, body, #root {
    height: 100%;
  }

  body {
    background: #f3f6f9;
    color: #0f172a;
    font-family: "IBM Plex Sans", "Inter", "Segoe UI", "PingFang SC", "Microsoft YaHei", Arial, sans-serif;
    text-rendering: optimizeLegibility;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  .colorWeak {
    filter: invert(80%);
  }

  .ant-layout {
    min-height: 100vh;
  }
  .ant-pro-sider.ant-layout-sider.ant-pro-sider-fixed {
    left: unset;
  }

  .ant-pro-sider {
    border-right: 1px solid rgba(148, 163, 184, 0.18);
    background: #f8fafc !important;
    box-shadow: none !important;
  }

  .ant-pro-sider .ant-layout-sider-children {
    background: linear-gradient(180deg, #fbfdff 0%, #f4f7fb 100%);
  }

  .ant-pro-sider .ant-pro-sider-logo {
    height: 46px;
    padding: 0 12px;
    border-bottom: 1px solid rgba(148, 163, 184, 0.14);
  }

  .ant-pro-sider .ant-pro-sider-logo h1 {
    color: #0f172a;
    font-size: 14px;
    font-weight: 700;
  }

  .ant-pro-sider .ant-menu {
    padding: 8px;
    background: transparent !important;
  }

  .ant-pro-sider .ant-menu-item,
  .ant-pro-sider .ant-menu-submenu-title {
    height: 34px;
    margin: 3px 0;
    padding-inline: 12px !important;
    border-radius: 6px;
    color: #334155;
    font-size: 13px;
    line-height: 34px;
  }

  .ant-pro-sider .ant-menu-item-selected {
    color: #0f172a;
    font-weight: 700;
    background: linear-gradient(90deg, rgba(30, 64, 175, 0.12) 0%, rgba(14, 165, 233, 0.08) 100%) !important;
    box-shadow: inset 3px 0 0 #1d4ed8;
  }

  .ant-pro-sider .ant-menu-item:hover,
  .ant-pro-sider .ant-menu-submenu-title:hover {
    color: #0f172a;
    background: rgba(30, 64, 175, 0.07) !important;
  }

  .ant-pro-sider .ant-menu-submenu-selected > .ant-menu-submenu-title {
    color: #0f172a;
    font-weight: 700;
    background: rgba(214, 170, 88, 0.1) !important;
    box-shadow: inset 3px 0 0 #d6aa58;
  }

  .ant-pro-sider .ant-menu-sub.ant-menu-inline {
    margin: 0 4px 5px 10px;
    padding: 3px 0 3px 7px;
    border-left: 1px solid rgba(30, 64, 175, 0.16);
    background: rgba(248, 250, 252, 0.72) !important;
  }

  .ant-pro-sider .ant-menu-sub.ant-menu-inline .ant-menu-item {
    height: 30px;
    margin: 2px 0;
    padding-inline: 10px !important;
    border-radius: 4px;
    font-size: 12px;
    line-height: 30px;
  }

  .ant-pro-sider .ant-menu-sub.ant-menu-inline .ant-menu-item-selected {
    color: #0f172a;
    background: linear-gradient(90deg, rgba(30, 64, 175, 0.12), rgba(14, 165, 233, 0.05)) !important;
    box-shadow: inset 2px 0 0 #1d4ed8;
  }

  .fractal-research-shell {
    box-sizing: border-box;
    width: 100%;
    max-width: none;
    margin: 0;
    padding: 0 24px 24px;
  }

  .fractal-research-content {
    min-width: 0;
  }

  .fractal-research-rail {
    min-width: 0;
    position: sticky;
    top: 12px;
    align-self: start;
    max-height: calc(100vh - 24px);
    overflow-y: auto;
    padding-right: 2px;
  }

  @media (max-width: 1440px) {
    .fractal-research-shell {
      grid-template-columns: minmax(0, 1fr) !important;
      padding: 0 16px 20px;
    }

    .fractal-research-rail {
      display: none;
    }
  }

  @media (max-width: 768px) {
    .ant-pro-layout-content {
      padding-inline: 0 !important;
    }

    .fractal-research-shell {
      padding: 0 12px 16px;
    }
  }

  canvas {
    display: block;
  }

  ul, ol {
    list-style: none;
  }

  @media (max-width: 768px) {
    .ant-table {
      width: 100%;
      overflow-x: auto;
      &-thead > tr,
      &-tbody > tr {
        > th,
        > td {
          white-space: pre;
          > span {
            display: block;
          }
        }
      }
    }
  }

  // Compatible with IE11
  @media screen and(-ms-high-contrast: active), (-ms-high-contrast: none) {
    body .ant-design-pro > .ant-layout {
      min-height: 100vh;
    }
  }
`;

export default GlobalStyle;
